import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles, ChevronDown } from 'lucide-react';
import KIAssessment from './KIAssessment';


const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isDarkHero = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const serviceLinks = [
    { name: 'Prozessanalyse & Automatisierung', href: '/prozessanalyse' },
    { name: 'KI-Tool-Entwicklung', href: '/ki-tool-entwicklung' },
    { name: 'Geschäftsideen & Start-Ups', href: '/geschaeftsideen' },
    { name: 'Systeme & Landingpages', href: '/systeme-landingpages' },
    { name: 'Individuelle Lösungen', href: '/individuelle-loesungen' },
  ];

  const isServiceActive = serviceLinks.some(l => location.pathname === l.href);
  const isActive = (href) => location.pathname === href;

  const linkClass = (active) =>
    `px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
      active
        ? isDarkHero ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-50'
        : isDarkHero ? 'text-gray-300 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
    }`;

  return (
    <>
      <nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isDarkHero
            ? isScrolled
              ? 'bg-[#020405]/90 backdrop-blur-xl border-b border-white/5'
              : 'bg-transparent'
            : isScrolled
              ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-100'
              : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo" className="flex items-center">
              <img 
                src={isDarkHero && !isScrolled ? '/logo-white.png' : '/logo.png'}
                alt="LAIzyones – KI-Agentur" 
                className="h-12 md:h-14 object-contain transition-all duration-300"
              />
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/" data-testid="nav-startseite" className={linkClass(isActive('/'))}>Startseite</Link>

              {/* Leistungen Dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  data-testid="nav-leistungen"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`${linkClass(isServiceActive)} inline-flex items-center gap-1`}
                >
                  Leistungen
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDropdownOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-72 rounded-xl shadow-xl border overflow-hidden z-50 ${
                    isDarkHero ? 'bg-[#0B1221] border-white/10' : 'bg-white border-gray-200'
                  }`}>
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        data-testid={`nav-service-${link.href.slice(1)}`}
                        className={`block px-4 py-3 text-sm font-medium transition-colors ${
                          isActive(link.href)
                            ? 'text-emerald-500 bg-emerald-500/10'
                            : isDarkHero
                              ? 'text-gray-300 hover:text-white hover:bg-white/5'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/produkte" data-testid="nav-produkte" className={linkClass(isActive('/produkte'))}>Produkte</Link>
              <Link to="/blog" data-testid="nav-blog" className={linkClass(isActive('/blog') || location.pathname.startsWith('/blog/'))}>Blog</Link>
              <Link to="/ueber-uns" data-testid="nav-über-uns" className={linkClass(isActive('/ueber-uns'))}>Über uns</Link>
            </div>

            {/* CTA */}
            <div className="hidden md:block">
              <button
                data-testid="nav-assessment-btn"
                className="btn-primary text-sm"
                onClick={() => setIsAssessmentOpen(true)}
              >
                <Sparkles className="w-4 h-4 relative z-10" />
                <span>KI-Assessment</span>
              </button>
            </div>

            {/* Mobile toggle */}
            <button
              data-testid="mobile-menu-btn"
              className={`md:hidden p-2 rounded-lg transition-colors ${isDarkHero ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden fixed inset-0 top-20 bg-[#020405] z-40 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}>
          <div className="flex flex-col items-center justify-start pt-8 gap-2 px-6 overflow-y-auto max-h-[calc(100vh-5rem)]">
            <Link to="/" className={`text-lg font-['Space_Grotesk'] font-semibold px-6 py-3 rounded-xl transition-colors ${isActive('/') ? 'text-emerald-400 bg-emerald-400/10' : 'text-gray-300 hover:text-white'}`}>
              Startseite
            </Link>
            <span className="text-xs text-gray-500 uppercase tracking-wider mt-4 mb-1">Leistungen</span>
            {serviceLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-base font-['Space_Grotesk'] font-medium px-6 py-2.5 rounded-xl transition-colors ${
                  isActive(link.href) ? 'text-emerald-400 bg-emerald-400/10' : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/produkte" className={`text-lg font-['Space_Grotesk'] font-semibold px-6 py-3 rounded-xl transition-colors mt-2 ${isActive('/produkte') ? 'text-emerald-400 bg-emerald-400/10' : 'text-gray-300 hover:text-white'}`}>
              Produkte
            </Link>
            <Link to="/blog" className={`text-lg font-['Space_Grotesk'] font-semibold px-6 py-3 rounded-xl transition-colors ${isActive('/blog') || location.pathname.startsWith('/blog/') ? 'text-emerald-400 bg-emerald-400/10' : 'text-gray-300 hover:text-white'}`}>
              Blog
            </Link>
            <Link to="/ueber-uns" className={`text-lg font-['Space_Grotesk'] font-semibold px-6 py-3 rounded-xl transition-colors ${isActive('/ueber-uns') ? 'text-emerald-400 bg-emerald-400/10' : 'text-gray-300 hover:text-white'}`}>
              Über uns
            </Link>
            <button
              onClick={() => { setIsMobileMenuOpen(false); setIsAssessmentOpen(true); }}
              className="btn-primary text-base mt-4"
            >
              <Sparkles className="w-4 h-4 relative z-10" />
              <span>KI-Assessment</span>
            </button>
          </div>
        </div>
      </nav>

      <KIAssessment isOpen={isAssessmentOpen} onClose={() => setIsAssessmentOpen(false)} />
    </>
  );
};

export default Navigation;
